import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { CmmStpStatus } from "../../../enums/cmmStpStatus";
import { PaStatus } from "../../../enums/paStatus";
import {
  SecondStpStatus,
  SecondStpStatusLabels,
} from "../../../enums/secondStpStatus";
import { OncologySubmissionTableColumnKeys } from "../../../enums/tableColumnKeys";
import { mapPharmaStpFileExtendedRowFields } from "../../pharmaStpFile/table/mapPharmaStpFileExtendedRowFields";
import { getStpFileStatus } from "../../pharmaStpFile/table/utils/getCorrectStatus";
import getPoc from "../../pharmaStpFile/table/utils/getPoc";
import { getSftpBadgeStyles } from "../../pharmaStpFile/table/utils/getSftpBadgeStyles";
import {
  getBgColorForStpStatus,
  getTextColorForStpStatus,
} from "../../pharmaStpFile/table/utils/getStpStatusColors";
import { getSentToClientBadgeStyles } from "./getSentToClientBadgeStyles";
import {
  formatDobToDisplay,
  formatTimestampToDisplay,
} from "./utils/formatDobToDisplay";
import { getAsteraOutcomeDisplayText } from "./utils/getAsteraOutcomeDisplayText";
import { getAsteraBiosimilarBadgeValue } from "./utils/getAsteraBiosimilarBadgeValue";
import { getAsteraCaseAppealIconValue } from "./utils/getAsteraCaseAppealIconValue";
import { getPaStatusBucketBadgeStyles } from "./utils/getPaStatusBucketBadgeStyles";
import { getRouteViaBadgeStyles } from "./utils/getRouteViaBadgeStyles";
import { isAsteraOutcomeViewDisabled } from "./utils/isAsteraOutcomeViewDisabled";

export const AsteraOncologyExternalTableHeader: TableHeader[] = [
  {
    label: "RxNumber",
    key: OncologySubmissionTableColumnKeys.VIEW,
    order: 1,
    width: 8,
    sortable: false,
    type: TableCellType.BUTTON_LINK,
  },
  {
    label: "Date Of Work",
    key: OncologySubmissionTableColumnKeys.DATE_OF_WORK,
    order: 2,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Patient Details",
    key: OncologySubmissionTableColumnKeys.PATIENT_NAME,
    order: 3,
    width: 10,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: "Insurance",
    key: OncologySubmissionTableColumnKeys.INSURANCE_ID,
    order: 4,
    width: 14,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: "Drug",
    key: OncologySubmissionTableColumnKeys.DRUG,
    order: 5,
    width: 18,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Case ID",
    key: OncologySubmissionTableColumnKeys.CMM_ID,
    order: 6,
    width: 10,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Outcome",
    key: OncologySubmissionTableColumnKeys.RESPONSE_STATUS,
    order: 7,
    width: 12,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Outcome Class",
    key: OncologySubmissionTableColumnKeys.PA_STATUS_BUCKET,
    order: 8,
    width: 14,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Case Appeal",
    key: OncologySubmissionTableColumnKeys.CASE_APPEAL,
    order: 9,
    width: 8,
    sortable: false,
    type: TableCellType.CASE_APPEAL_ICON,
  },
  {
    label: "Biosimilar",
    key: OncologySubmissionTableColumnKeys.BIOSIMILAR_CASE,
    order: 10,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Summary",
    key: OncologySubmissionTableColumnKeys.CMM,
    order: 11,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
];

export const generateTableDataForAsteraOncologyExternal = (
  data: PharmaStpFileModel[],
): OncologySubmissionTableDataModel[] => {
  return data.map((item) => {
    const fileName: string = item?.filename ?? "";
    const dow: string = formatTimestampToDisplay(item?.dumped_at);
    const identifier: string = item?.identifier ?? "";

    // Checkbox data for batch column (empty label to show only checkbox)
    const batch = {
      id: identifier,
      label: "",
      isChecked: false,
      disabled: false,
    };
    const patient_mrn: string = item?.patient_mrn ?? "";
    const patientName: TableCellMultiline = {
      mainText: item?.patient_name ?? "",
      secondaryText: `DOB : ${formatDobToDisplay(item?.dob)}`,
      hideCopyIcon: true,
    };

    const sequence: string =
      String(item?.sequence ?? item?.seq ?? "").trim() || "";

    const routeVia: TableCellWithBadge = getRouteViaBadgeStyles(
      item?.route_via,
    );
    const drug: string = item?.drug ?? "";

    const cmmIdValue = item?.covermymed_id;
    const cmmId: string =
      !cmmIdValue || cmmIdValue === "nan" || cmmIdValue === "NaN"
        ? ""
        : String(cmmIdValue);

    const poc: string = getPoc(item?.poc ?? "");

    const isAddtext = item?.covermymed_id ? "Edit" : "Add";
    const add = {
      value: isAddtext,
      isDisabled: false,
      title: isAddtext,
    };

    const sftpStyles = getSftpBadgeStyles(item?.sftp_status);
    const sftp: TableCellWithBadge = {
      text: sftpStyles.title,
      color: sftpStyles.textColor,
      bgColor: sftpStyles.bgColor,
      hoverText: sftpStyles.title,
    };

    const outcomeClassValue =
      item?.response_status === PaStatus.APPROVED
        ? null
        : item?.pa_outcome_classification;

    const paStatusBucketStyles =
      getPaStatusBucketBadgeStyles(outcomeClassValue);
    const paStatusBucket: TableCellWithBadge = {
      text: paStatusBucketStyles.text,
      color: paStatusBucketStyles.textColor,
      bgColor: paStatusBucketStyles.bgColor,
      hoverText: paStatusBucketStyles.hoverText,
    };

    const paStatusReason: string = item?.pa_status_reason ?? "--";
    const comment: string = item?.comments ?? "";

    const secondStpStatusBadge: TableCellWithBadge = item?.second_stp_status
      ? {
          text:
            SecondStpStatusLabels[item.second_stp_status as SecondStpStatus] ??
            item.second_stp_status,
          color:
            getTextColorForStpStatus(item.second_stp_status as CmmStpStatus) ??
            "#111111",
          bgColor:
            getBgColorForStpStatus(item.second_stp_status as CmmStpStatus) ??
            "#F5F5F5",
        }
      : {
          text: "Not Sent",
          color: "#111111",
          bgColor: "#F5F5F5",
        };

    const rawResponseStatus = item?.response_status ?? null;
    const outcomeDisplayText = getAsteraOutcomeDisplayText(rawResponseStatus);
    const responseStpStatusBadge: TableCellWithBadge = rawResponseStatus
      ? {
          ...getStpFileStatus(rawResponseStatus),
          text: outcomeDisplayText,
        }
      : {
          text: outcomeDisplayText,
          color: "#665D00",
          bgColor: "#FFFBEB",
        };

    const insuranceId: TableCellMultiline = {
      mainText: item?.pharmacy ?? "",
      secondaryText: `ID : ${item?.insuranceid ?? ""}`,
      hideCopyIcon: true,
    };
    const cmm: TableCellWithBadge = {
      text: "View",
      color: "#FFFFFF",
      bgColor: "#111111",
      isDisabled: isAsteraOutcomeViewDisabled(rawResponseStatus),
    };
    const sentToClient: TableCellWithBadge = getSentToClientBadgeStyles(
      item.sent_to_client ?? null,
    );

    const caseAppeal = getAsteraCaseAppealIconValue(item);
    const biosimilarCase = getAsteraBiosimilarBadgeValue(item);

    const binValue = item?.bin;
    const bin: string =
      !binValue || binValue === "nan" || binValue === "NaN"
        ? ""
        : String(binValue);

    const extendedFields = mapPharmaStpFileExtendedRowFields(item);

    return {
      fileName,
      sequence,
      batch,
      dow,
      patient_mrn,
      patientName,
      routeVia,
      drug,
      cmm,
      sentToClient,
      bin,
      insuranceId,
      paStatusBucket,
      caseAppeal,
      biosimilarCase,
      cmmId,
      secondStpStatus: secondStpStatusBadge,
      responseStpStatus: responseStpStatusBadge,
      add,
      poc,
      sftp,
      identifier,
      textNoteStatus: item.text_note_status,
      cmmStatus: item.meta?.cmm_st_rpa_status,
      paStatusReason: paStatusReason,
      comment: comment,
      view: sequence || "View",
      routeViaValue: item?.route_via ?? "",
      responseStatusValue: item?.response_status ?? "",
      patient_name: item.patient_name ?? "",
      dob: item.dob ?? "",
      seq: item.seq ?? "",
      pharmacy_type: item.pharmacy_type ?? "",
      provider_name: item.provider_name ?? "",
      insuranceid: item.insuranceid ?? null,
      pharmacy: item.pharmacy ?? null,
      covermymed_id: item.covermymed_id ?? null,
      filename: item.filename ?? "",
      org_id: item.org_id ?? "",
      dumped_at: item.dumped_at ?? "",
      meta: item.meta,
      rx_due_date: item.rx_due_date ?? null,
      pharmacy_phone: item.pharmacy_phone ?? null,
      sftp_status: item.sftp_status ?? null,
      second_stp_status: item.second_stp_status ?? null,
      response_status: item.response_status ?? null,
      route_via: item.route_via ?? null,
      pa_status_reason: item.pa_status_reason ?? null,
      comments: item.comments ?? null,
      ...extendedFields,
    };
  });
};
